import axios from 'axios';
import configFile from '../config.js';

// Create axios instance with default config
const createOAuthClient = () => {
  const baseUrl = configFile.serviceUrl;
  
  return axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${configFile.apiKey}`,
    },
  });
};

/**
 * Get list of supported OAuth providers
 * @returns {Promise<Array>} List of provider objects
 */
export const getProviders = async () => {
  try {
    const oauthClient = createOAuthClient();
    const response = await oauthClient.get('/providers');
    
    const responseData = response.data;
    
    if (responseData?.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching OAuth providers:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch providers');
  }
};

/**
 * Get authorization URL for OAuth flow
 * @param {string} provider - The provider name (e.g., 'quickbooks', 'hubspot')
 * @param {string} userId - The user ID from auth context
 * @returns {Promise<string>} Authorization URL to open in popup
 */
export const getAuthorizationUrl = async (provider, userId) => {
  try {
    const oauthClient = createOAuthClient();
    const response = await oauthClient.get('/authorize', {
      params: {
        provider: provider,
        user_id: userId,
      },
    });
    
    const responseData = response.data;
    
    if (responseData?.data?.authorization_url) {
      return responseData.data.authorization_url;
    }
    
    if (responseData?.authorization_url) {
      return responseData.authorization_url;
    }
    
    if (responseData?.url) {
      return responseData.url;
    }
    
    if (typeof responseData === 'string') {
      return responseData;
    }
    
    throw new Error('Authorization URL not found in response');
  } catch (error) {
    console.error('Error getting authorization URL:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to get authorization URL');
  }
};

/**
 * Get all OAuth connections for the current user
 * @param {string} userId - The user ID from auth context
 * @returns {Promise<Array>} List of connection objects
 */
export const getConnections = async (userId) => {
  try {
    const oauthClient = createOAuthClient();
    const response = await oauthClient.get('/connections', {
      params: {
        user_id: userId,
      },
    });
    
    const responseData = response.data;
    
    if (responseData?.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching OAuth connections:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch connections');
  }
};

/**
 * Get OAuth connection status for a specific provider
 * @param {string} provider - The provider name (e.g., 'quickbooks', 'hubspot')
 * @param {string} userId - The user ID from auth context
 * @returns {Promise<Object>} Connection status object
 */
export const getConnectionStatus = async (provider, userId) => {
  try {
    const oauthClient = createOAuthClient();
    const response = await oauthClient.get('/status', {
      params: {
        provider: provider,
        user_id: userId,
      },
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error fetching connection status:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch connection status');
  }
};

/**
 * Disconnect an OAuth connection for a provider
 * @param {string} provider - The provider name (e.g., 'quickbooks', 'hubspot')
 * @param {string} userId - The user ID from auth context
 * @returns {Promise<Object>} Disconnect result
 */
export const disconnectConnection = async (provider, userId) => {
  try {
    const oauthClient = createOAuthClient();
    const response = await oauthClient.delete('/connections', {
      params: {
        provider: provider,
        user_id: userId,
      },
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error disconnecting:', error);
    throw new Error(error.response?.data?.message || 'Failed to disconnect');
  }
};

/**
 * Get display name for a provider
 * @param {string} provider - Provider key (e.g., 'quickbooks')
 * @param {Array} providersList - Optional list of providers from API
 * @returns {string} Display name (e.g., 'QuickBooks')
 */
export const getProviderDisplayName = (provider, providersList = []) => {
  const providerData = providersList.find(
    p => p.name?.toLowerCase() === provider?.toLowerCase()
  );
  if (providerData?.display_name) {
    return providerData.display_name;
  }
  
  const fallbackNames = {
    quickbooks: 'QuickBooks',
    hubspot: 'HubSpot',
  };
  
  return fallbackNames[provider?.toLowerCase()] || provider;
};